import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/_dashboard.scss";
import InputsPanel from "../components/InputsPanel";
import EstimatedPerformance from "../components/EstimatedPerformance";
import OtherCombinations from "../components/OtherCombinations";
import alertify from "alertifyjs";
import "alertifyjs/build/css/alertify.css";
import "alertifyjs/build/css/themes/default.css";
import apiService from "../apiService";

const Dashboard = () => {
      const [currentTab, setCurrentTab] = useState("wear-parts");
      const [unit, setUnit] = useState("metric");
      const [noNozzles, setNoNozzles] = useState(3);
      const [pressure, setPressure] = useState(120);
      const [orificeDiscSize, setOrificeDiscSize] = useState(27);
      const [swirlType, setSwirlType] = useState(5);
      const [flowRate, setFlowRate] = useState(null);
      const [showOrificeDiscSizeOptions, setShowOrificeDiscSizeOptions] = useState(false);
      const [showSwirlTypeOptions, setShowSwirlTypeOptions] = useState(false);
      const [showResults, setShowResults] = useState(false);
      const [estimatedFlowRate, setEstimatedFlowRate] = useState(null);
      const [estimatedOrificeDiscSize, setEstimatedOrificeDiscSize] = useState(null);
      const [estimatedDropletSize, setEstimatedDropletSize] = useState(null);
      const [estimatedSprayAngle, setEstimatedSprayAngle] = useState(null);
      const [results, setResults] = useState(null);
      const [combinations, setCombinations] = useState([]);
      const [orificeDiscOptions, setOrificeDiscOptions] = useState([]);
      const [swirlTypeOptions, setSwirlTypeOptions] = useState([]);
      const navigate = useNavigate();

      useEffect(() => {
            const saved = JSON.parse(localStorage.getItem("dashboardState"));
            if (saved) {
                  setUnit(saved.unit || "metric");
                  setNoNozzles(saved.noNozzles || 3);
                  setPressure(saved.pressure || 120);
                  setOrificeDiscSize(saved.orificeDiscSize || 27);
                  setSwirlType(saved.swirlType || 5);
                  setFlowRate(saved.flowRate || null);
            }

            apiService.get("/api/calculator-options/")
                  .then((options) => {
                        setOrificeDiscOptions(options.orifice_discs || []);
                        setSwirlTypeOptions(options.swirl_types || []);
                  })
                  .catch((error) => console.error("Could not load calculator options:", error));
      }, []);

      useEffect(() => {
            const state = {
                  unit,
                  noNozzles,
                  pressure,
                  orificeDiscSize,
                  swirlType,
                  flowRate,
            };
            localStorage.setItem("dashboardState", JSON.stringify(state));
      }, [unit, noNozzles, pressure, orificeDiscSize, swirlType, flowRate]);

      const handleLogout = () => {
            localStorage.removeItem("authToken");
            navigate("/", { replace: true });
      };

      const resetDefaults = () => {
            setUnit("metric");
            setNoNozzles(3);
            setPressure(120);
            setOrificeDiscSize(27);
            setSwirlType(5);
            setFlowRate(null);
            setShowResults(false);
            setEstimatedFlowRate(null);
            setEstimatedOrificeDiscSize(null);
            setEstimatedDropletSize(null);
            setEstimatedSprayAngle(null);
            setCombinations([]);
            localStorage.removeItem("dashboardState");
      };

      const changePressure = (value) => {
            const regex = /^(0|[1-9]\d*)(\.\d{0,2})?$/;
            if (value === "" || regex.test(value)) {
                  setPressure(value);
            }
      };

      const changeOrificeDiscSize = (value) => {
            setOrificeDiscSize(value);
            setShowOrificeDiscSizeOptions(false);
      };

      const changeSwirlType = (value) => {
            setSwirlType(value);
            setShowSwirlTypeOptions(false);
      };

      const changeFlowRate = (value) => {
            const regex = /^(0|[1-9]\d*)(\.\d{0,2})?$/;
            if (value === "") {
                  setFlowRate(null);
            } else if (regex.test(value)) {
                  setFlowRate(value);
            }
      };

      const onCalculate = async () => {
            if (!noNozzles || noNozzles <= 0) {
                  alertify.alert("Error", "Number of nozzles must be greater than 0");
                  return;
            }
            if (!pressure || pressure <= 0) {
                  alertify.alert("Error", "Pressure must be greater than 0");
                  return;
            }

            let calcType = "";
            if (currentTab === "wear-parts") calcType = "WEAR_PARTS";
            if (currentTab === "swirl-flow") calcType = "SWIRL_FLOW";
            if (currentTab === "flow-pressure") calcType = "FLOW_PRESSURE";

            const requestData = {
                  calc: calcType,
                  nozzles: noNozzles,
                  pressure: parseFloat(pressure),
                  orifice_disc: orificeDiscSize || null,
                  swirl_type: swirlType || null,
                  flow: flowRate ? parseFloat(flowRate) : null,
                  unit_scale: unit === "metric" ? 0 : 1,
            };

            try {
                  const response = await apiService.post("/api/calculate/", JSON.stringify(requestData), {
                        "Content-Type": "application/json",
                  });

                  if (!response) throw new Error("Calculation failed");

                  const data = await response;

                  if (data.error) {
                        alertify.alert("Error", data.error);
                        setShowResults(false);
                        return;
                  }

                  if (!data.combinations?.length) {
                        alertify.alert("No Results", "No valid combinations found for given parameters.");
                        setShowResults(false);
                        return;
                  }

                  setEstimatedFlowRate(data.flow_rate || null);
                  setEstimatedDropletSize(data.droplet_size || null);
                  setEstimatedSprayAngle(data.angle || null);
                  if (currentTab === "swirl-flow" && data?.orifice_disc_size) {
                        setEstimatedOrificeDiscSize(data.orifice_disc_size);
                  } else {
                        setEstimatedOrificeDiscSize(null);
                  }
                  if (data.results_description) setResults(data.results_description);
                  setCombinations(data.combinations);
                  setShowResults(true);
            } catch (err) {
                  alertify.alert("Error", err.message || "Calculation failed");
                  console.error("Error in calculation:", err);
                  setShowResults(false);
            }
      };

      const changeTab = (newTab) => {
            setCurrentTab(newTab);
            setShowResults(false);
            setEstimatedFlowRate(null);
            setEstimatedOrificeDiscSize(null);
            setEstimatedDropletSize(null);
            setEstimatedSprayAngle(null);
            setCombinations([]);

            if (newTab === "wear-parts") {
                  setOrificeDiscSize(27);
                  setSwirlType(5);
                  setFlowRate(null);
            } else if (newTab === "swirl-flow") {
                  setFlowRate(140);
                  setSwirlType(5);
                  setOrificeDiscSize(null);
            } else if (newTab === "flow-pressure") {
                  setFlowRate(140);
                  setSwirlType(null);
                  setOrificeDiscSize(null);
            }

            localStorage.removeItem("dashboardState");
      };

      return (
            <div className="dashboard-container">
                  <div className="dashboard-box">
                        <div className="title-row">
                              <img src="/images/logo.png" alt="Logo" className="logo" />
                              <div className="header-buttons">
                                    <div className="reset-container">
                                          <button className="reset-btn" onClick={resetDefaults}>
                                                RESET
                                          </button>
                                    </div>
                                    <div className="logout-container" onClick={handleLogout}>
                                          <span>LOGOUT</span>
                                          <img src="/images/logout.png" alt="Logout" />
                                    </div>
                              </div>
                        </div>

                        <div className="sections-row">
                              <div
                                    className={`section-tab ${currentTab === "wear-parts" && "focused-tab"}`}
                                    onClick={() => changeTab("wear-parts")}
                              >
                                    <img src="/images/wear-parts.png" alt="Wear Parts" />
                                    <h1>WEAR PARTS</h1>
                                    <p>
                                          Select a system of nozzles, an operating pressure and a swirl-orifice disc
                                          combination. Output an estimated flow rate, droplet size and spray angle.
                                    </p>
                              </div>
                              <div
                                    className={`section-tab ${currentTab === "swirl-flow" && "focused-tab"}`}
                                    onClick={() => changeTab("swirl-flow")}
                              >
                                    <img src="/images/swirl-flow.png" alt="Swirl Flow" />
                                    <h1>SWIRL & FLOW</h1>
                                    <p>
                                          Select a system of nozzles, an operating pressure, system flow rate, and swirl
                                          type. Output the required orifice disc, droplet size, and spray angle.
                                    </p>
                              </div>
                              <div
                                    className={`section-tab ${currentTab === "flow-pressure" && "focused-tab"}`}
                                    onClick={() => changeTab("flow-pressure")}
                              >
                                    <img src="/images/flow-pressure.png" alt="Flow Pressure" />
                                    <h1>FLOW & PRESSURE</h1>
                                    <p>
                                          Select a system of nozzles, an operating pressure, and system flow rate.
                                          Output a list of swirl-orifice disc combinations and their characteristics.
                                    </p>
                              </div>
                        </div>

                        {/* 🔹 Calculator */}
                        <div className="calculator-section">
                              <InputsPanel
                                    unit={unit}
                                    currentTab={currentTab}
                                    setUnit={setUnit}
                                    noNozzles={noNozzles}
                                    setNoNozzles={setNoNozzles}
                                    pressure={pressure}
                                    changePressure={changePressure}
                                    orificeDiscSize={orificeDiscSize}
                                    changeOrificeDiscSize={changeOrificeDiscSize}
                                    flowRate={flowRate}
                                    changeFlowRate={changeFlowRate}
                                    showOrificeDiscSizeOptions={showOrificeDiscSizeOptions}
                                    setShowOrificeDiscSizeOptions={setShowOrificeDiscSizeOptions}
                                    swirlType={swirlType}
                                    changeSwirlType={changeSwirlType}
                                    showSwirlTypeOptions={showSwirlTypeOptions}
                                    setShowSwirlTypeOptions={setShowSwirlTypeOptions}
                                    onCalculate={onCalculate}
                                    setResults={setResults}
                                    orificeDiscOptions={orificeDiscOptions.length ? orificeDiscOptions : undefined}
                                    swirlTypeOptions={swirlTypeOptions.length ? swirlTypeOptions : undefined}
                              />

                              <div className="results">
                                    {!showResults && <p>Results will display here.</p>}
                                    {showResults && (currentTab === "wear-parts" || currentTab === "swirl-flow") && (
                                          <EstimatedPerformance
                                                unit={unit}
                                                flowRate={estimatedFlowRate}
                                                dropletSize={estimatedDropletSize}
                                                sprayAngle={estimatedSprayAngle}
                                                orificeDiscSize={estimatedOrificeDiscSize}
                                                currentTab={currentTab}
                                          />
                                    )}
                                    {showResults && (
                                          <OtherCombinations
                                                unit={unit}
                                                isOtherCombinations={
                                                      currentTab === "wear-parts" || currentTab === "swirl-flow"
                                                }
                                                combinations={combinations}
                                          />
                                    )}
                              </div>
                        </div>

                        {/* 🔹 Disclaimer */}
                        <p className="disclaimer">
                              Disclaimer: This application is intended as a comparative tool for comparing wear part
                              combinations. The models used in the application are based on testing with water and
                              standard temperature and pressure. Values output results may not reflect application with
                              other fluid mediums.
                        </p>
                  </div>

                  <div className="footer">
                        <div className="footer-links">
                              <a
                                    href="https://spraynozzle.com.au/contact/"
                                    className="footer-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                              >
                                    Contact Us
                              </a>
                              <span className="vertical-bar">|</span>
                              <Link to="/privacy-policy" className="footer-link">
                                    Privacy Policy
                              </Link>
                              <span className="vertical-bar">|</span>
                              <Link to="/terms-conditions" className="footer-link">
                                    Terms & Conditions
                              </Link>
                        </div>
                        <div className="copyright">©2025 Spray Nozzle Engineering | All Rights Reserved</div>
                  </div>
            </div>
      );
};

export default Dashboard;
